import { defaultDefinition, type ReportDefinition, type ReportFolder, type ReportType } from '@olytic/foundation/reports';
import { useCallback, useEffect, useState } from 'react';

// Saved reports and their folders, from the server (apps/server/src/savedReports.mjs,
// tables ops.report_folder and ops.saved_report). The server decides who sees,
// adds to, changes and shares each folder; everything arrives with the caller's
// rights, and this file only carries them.
//
// Rules (the list-view model, applied to folders):
// - Anyone creates private folders, folders inside folders they can edit, and
//   reports in them. The owner shares a top-level folder with named people;
//   shared means editable. Folders inside take the top-level folder's sharing.
// - Only admins create public (everyone) folders, and only admins add to,
//   change or delete anything in one.
// - A report stays in the folder it was saved to until someone who can edit
//   both folders moves it; only admins move into a public folder.

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export interface Person { id: string; name: string }

export interface SavedReport {
  id: string;
  name: string;
  description?: string;
  folderId: string;
  reportType: string;
  format?: ReportDefinition['format'];
  owner: string;
  ownerId: string;
  updatedBy?: string;
  updatedAt: string;
  /** When the caller last opened the report's own page; absent if they never have (MOTHER-304). */
  lastRunAt?: string;
  favorite: boolean;
  canEdit: boolean;
  /** Only when one report is opened. */
  definition?: ReportDefinition;
}

export interface ReportLibrary {
  me: Person & { admin: boolean };
  folders: ReportFolder[];
  reports: SavedReport[];
  favoriteTypes: string[];
  typeCounts: Record<string, number>;
  /** Who a folder can be shared with. */
  people: Person[];
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

async function read<T>(response: Response, fallback: string): Promise<T> {
  const payload = response.status === 204 ? null : ((await response.json().catch(() => undefined)) as unknown);
  if (!response.ok) {
    const error = typeof payload === 'object' && payload !== null ? (payload as { error?: unknown }).error : undefined;
    throw new Error(typeof error === 'string' ? error : `${fallback} (${response.status}).`);
  }
  return payload as T;
}

export const loadLibrary = async (fetcher: Fetch): Promise<ReportLibrary> =>
  read<ReportLibrary>(await fetcher('/api/reports/library'), 'The reports could not be loaded');

export const loadReport = async (fetcher: Fetch, id: string): Promise<SavedReport> =>
  (await read<{ report: SavedReport }>(await fetcher(`/api/reports/saved/${encodeURIComponent(id)}`), 'The report could not be loaded')).report;

export const createReport = async (fetcher: Fetch, folderId: string, definition: ReportDefinition): Promise<SavedReport> =>
  (await read<{ report: SavedReport }>(await fetcher('/api/reports/saved', json('POST', { folderId, definition })), 'Could not save the report')).report;

export const saveReport = async (fetcher: Fetch, id: string, definition: ReportDefinition): Promise<SavedReport> =>
  (await read<{ report: SavedReport }>(await fetcher(`/api/reports/saved/${encodeURIComponent(id)}`, json('PUT', { definition })), 'Could not save the report')).report;

/** Moves a report to another folder; it keeps its id, so its URL and favorites stay. */
export const moveReport = async (fetcher: Fetch, id: string, folderId: string): Promise<SavedReport> =>
  (await read<{ report: SavedReport }>(await fetcher(`/api/reports/saved/${encodeURIComponent(id)}`, json('PATCH', { folderId })), 'Could not move the report')).report;

/**
 * Renames a saved report. The server changes its name and its definition's name and nothing
 * else: it stays in its folder with its id, favorites, type, columns and filters. Whoever can
 * edit the report's folder may rename it; the server refuses anyone else.
 */
export const renameReport = async (fetcher: Fetch, id: string, name: string): Promise<SavedReport> =>
  (await read<{ report: SavedReport }>(await fetcher(`/api/reports/saved/${encodeURIComponent(id)}`, json('PATCH', { name })), 'Could not rename the report')).report;

/** The longest report name the server saves (MAX_REPORT_NAME in apps/server/src/savedReports.mjs). */
export const MAX_REPORT_NAME = 200;

const COPY_SUFFIX = ' (Copy)';

/**
 * A copy's name: `<name> (Copy)`, with the name cut so the whole stays within the server's
 * limit (it counts UTF-16 units, as here). A cut never splits a character in two.
 */
export function copyName(name: string): string {
  let base = name.trim().slice(0, MAX_REPORT_NAME - COPY_SUFFIX.length);
  if (/[\uD800-\uDBFF]$/.test(base)) base = base.slice(0, -1);
  return `${base.trimEnd()}${COPY_SUFFIX}`;
}

/**
 * Copies a saved report into the folder it is in: the same definition under `<name> (Copy)`.
 * The server makes the caller its owner, with a new id, no favorite and no run history, and
 * refuses it when the caller cannot add to that folder.
 */
export async function duplicateReport(fetcher: Fetch, id: string): Promise<SavedReport> {
  const original = await loadReport(fetcher, id);
  if (!original.definition) throw new Error('The report could not be loaded.');
  const definition = { ...original.definition, name: copyName(original.definition.name || original.name) };
  return createReport(fetcher, original.folderId, definition);
}

export const deleteReport = async (fetcher: Fetch, id: string): Promise<void> => {
  await read<null>(await fetcher(`/api/reports/saved/${encodeURIComponent(id)}`, { method: 'DELETE' }), 'Could not delete the report');
};

export const setFavorite = async (fetcher: Fetch, id: string, on: boolean): Promise<void> => {
  await read<null>(await fetcher(`/api/reports/saved/${encodeURIComponent(id)}/favorite`, json('PUT', { on })), 'Could not change your favorites');
};

/**
 * Records that the caller opened this saved report (PUT /api/reports/saved/:id/run, answered 204),
 * so the library's Recent view lists it by when they last opened it. Rejects on any other answer;
 * the report page ignores that, since a missed stamp must never block or slow the report.
 */
export const markReportRun = async (fetcher: Fetch, id: string): Promise<void> => {
  await read<null>(await fetcher(`/api/reports/saved/${encodeURIComponent(id)}/run`, { method: 'PUT' }), 'Could not record the run');
};

export const setFavoriteType = async (fetcher: Fetch, type: string, on: boolean): Promise<void> => {
  await read<null>(await fetcher(`/api/reports/type-favorites/${encodeURIComponent(type)}`, json('PUT', { on })), 'Could not change your favorites');
};

export const createFolder = async (fetcher: Fetch, name: string, parentId: string | null, everyone: boolean): Promise<ReportFolder> =>
  (await read<{ folder: ReportFolder }>(await fetcher('/api/reports/folders', json('POST', { name, parentId, everyone })), 'Could not make the folder')).folder;

export const updateFolder = async (
  fetcher: Fetch,
  id: string,
  change: { name?: string; visibility?: ReportFolder['visibility']; sharedWith?: string[] },
): Promise<ReportFolder> =>
  (await read<{ folder: ReportFolder }>(await fetcher(`/api/reports/folders/${encodeURIComponent(id)}`, json('PATCH', change)), 'Could not change the folder')).folder;

export const deleteFolder = async (fetcher: Fetch, id: string): Promise<void> => {
  await read<null>(await fetcher(`/api/reports/folders/${encodeURIComponent(id)}`, { method: 'DELETE' }), 'Could not delete the folder');
};

/** The public folder the standard reports install into (STANDARD_FOLDER in apps/server/src/standardReports.mjs). */
export const STANDARD_FOLDER = 'Standard reports';

/**
 * Whether the library already holds the standard reports' folder, found as the server finds it:
 * a top-level public folder named `Standard reports`, ignoring case and surrounding spaces. A
 * private folder of that name, or one inside another folder, is not it.
 */
export const hasStandardFolder = (folders: ReportFolder[]): boolean =>
  folders.some((f) => !f.parentId && f.visibility === 'everyone'
    && f.name.trim().toLowerCase() === STANDARD_FOLDER.toLowerCase());

/**
 * Installs the standard reports (POST /api/reports/standard, no body): the server finds or makes
 * the public `Standard reports` folder and adds each standard report it doesn't hold yet. Admin
 * only; the server refuses anyone else. Answers the folder's id and the reports' names.
 */
export const installStandardReports = async (
  fetcher: Fetch,
): Promise<{ folderId: string; created: string[]; existing: string[] }> =>
  read(await fetcher('/api/reports/standard', { method: 'POST' }), 'Could not add the standard reports');

/**
 * What the Reports page says after an install, from how many reports the server added
 * (MOTHER-248): `Added 2 standard reports.`, `Added 1 standard report.`, or, when it added none,
 * `Standard reports are up to date.`.
 */
export const standardReportsNotice = (added: number): string =>
  added === 0 ? 'Standard reports are up to date.' : `Added ${added} standard report${added === 1 ? '' : 's'}.`;

/** A saved definition with no columns starts with the report type's own. */
export const withDefaultColumns = (definition: ReportDefinition, type: ReportType): ReportDefinition =>
  definition.columns.length > 0 ? definition : { ...definition, columns: defaultDefinition(type).columns };

/** The library, loaded once per page and again after each change. */
export function useLibrary(fetcher: Fetch): { library: ReportLibrary | null; error: string | null; reload: () => Promise<void> } {
  const [library, setLibrary] = useState<ReportLibrary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      setLibrary(await loadLibrary(fetcher));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The reports could not be loaded.');
    }
  }, [fetcher]);
  useEffect(() => { void reload(); }, [reload]);
  return { library, error, reload };
}
